// the import order here matters. If mainModule comes too late, the uTestV2 AngularJS module
// isn't loaded before downgraded-components attempts to access it, and the app doesn't run.
import 'zone.js';
import 'core-js/proposals/reflect-metadata';
import 'core-js/features/reflect';
import angular from 'angular';
import { mainModule } from './app'; // eslint-disable-line
import { enableProdMode, NgZone, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { setAngularJSGlobal } from '@angular/upgrade/static';
import { UIRouter, UrlService } from '@uirouter/core';
import LogRocket from 'logrocket';
import { environment } from './environments/environment';

import { AppModule } from './app/ng-app/app.module';

if (environment.production) {
  enableProdMode();
}

setAngularJSGlobal(angular);

// LogRocket.init() happens in ngDoBootstrap() in app.module.ts (fetches config via the
// AngularJS ConfigService, which isn't available until after upgrade.bootstrap() runs there).
// identify() is wired up separately here since it doesn't need to block bootstrap.
//
// UserService.me() only issues its request once $rootScope.csrfCookieName is set, which
// index.ts's own run block sets asynchronously after its own ConfigService.config() call
// resolves. Chaining off ConfigService.config() here first (it's cached, so this doesn't
// trigger a second request) guarantees we run after that assignment instead of racing it --
// index.ts's run block is registered first, so its continuation on the shared cached promise
// always fires before this one.
mainModule.run(['UserService', 'ConfigService', 'CookieAgreementService', (UserService: any, ConfigService: any, CookieAgreementService: any) => {
  ConfigService.config().then((config: any) => {
    // identify() has to clear exactly the same gates init() does in app.module.ts (ENG-1083):
    // LogRocket configured on for this environment, and OneTrust Performance-cookie consent.
    // The SDK buffers calls made before init(), so never sending the ID is not the same thing
    // as never handing it over -- don't call identify() at all unless init() could have run.
    if (!config?.data?.logrocket_enabled
      || !config?.data?.logrocket_app_id
      || !CookieAgreementService.hasAcceptedPerformanceCookies()) {
      return null;
    }
    return UserService.me();
  }).then((res: any) => {
    // Re-checked rather than relying on the guard above: me() is in flight for a while, and
    // CookieAgreementService only starts watching for consent changes (and reloading the page
    // on one) after FeatureToggleService.getEnabledFeatures() resolves, so consent can be
    // withdrawn in between without the page going away underneath us.
    if (CookieAgreementService.hasAcceptedPerformanceCookies() && res?.data?.id != null) {
      LogRocket.identify(String(res.data.id));
    }
  }).catch(() => {
    // Not logged in, or the request failed -- nothing to identify.
  });
}]);

mainModule.config(['$urlServiceProvider', ($urlService: UrlService) => $urlService.deferIntercept()]);

platformBrowserDynamic().bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] }).then(platformRef => {
  const { urlService }: { urlService: UrlService } = platformRef.injector.get(UIRouter);

  function startUIRouter() {
    urlService.listen();
    urlService.sync();
  }

  platformRef.injector.get<NgZone>(NgZone).run(startUIRouter);
});
